import React, { useEffect } from "react";
import { connect } from "react-redux";
import {
    createStyles,
    Theme,
    WithStyles,
    withStyles,
    MenuItem,
    ListItemText,
    FormControlLabel,
    Switch,
    FormHelperText
} from "@material-ui/core";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { useGetUserListQuery } from "../../../../api/accountApiSlice";
import IUserInfo from "../../../../models/account/IUserInfo";
import { matchSorter } from "match-sorter";
import LoaderButton from "../../../shared/LoaderButton";
import { Save } from "@material-ui/icons";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
    useGetAuctionByUrlQuery,
    useUpdateAuctionMutation
} from "../../../../api/auctionApiSlice";
import { Controller, useForm, useWatch } from "react-hook-form";
import { createPatch } from "rfc6902";
import { enqueueSnackbar } from "notistack";
import { useAppDispatch } from "../../../../store/hooks";
import * as AuctionStore from "../../../../store/AuctionStore";
import { IAppDispatch, IAppState } from "../../../../store";
import { bindActionCreators } from "redux";

type IApprovedAuctionBiddersTab = WithStyles<typeof styles> &
    AuctionStore.IAuctionStoreState &
    typeof AuctionStore.actionCreators & {
        auctionLive: boolean;
        auctionUrl: string;
    };

const styles = (theme: Theme) =>
    createStyles({
        formSection: {
            marginBottom: theme.spacing(5),
            backgroundColor: theme.palette.common.black,
            padding: theme.spacing(4)
        },
        allowedBidderMenuItem: {
            backgroundColor: "transparent !important",
            margin: 0
        },
        allowedBidderSelect: {
            marginTop: theme.spacing(4)
        },
        saveButton: {
            marginTop: theme.spacing(4)
        }
    });

const schema = z.object({
    isApprovalRequired: z.boolean(),
    approvedBidders: z.array(z.string())
});

interface IApprovedAuctionBiddersFormValues extends z.infer<typeof schema> {}

function ApprovedAuctionBiddersTab({
    classes,
    auctionLive,
    auctionUrl
}: IApprovedAuctionBiddersTab) {
    const dispatch = useAppDispatch();

    const { control, setValue, setError } =
        useForm<IApprovedAuctionBiddersFormValues>({
            defaultValues: {
                approvedBidders: [],
                isApprovalRequired: false
            },
            resolver: zodResolver(schema)
        });

    const isApprovalRequiredFormValue = useWatch({
        name: "isApprovalRequired",
        control
    });

    const approvedBiddersFormValue = useWatch({
        name: "approvedBidders",
        control
    });

    const auctionByUrlQuery = useGetAuctionByUrlQuery(auctionUrl);
    const getUserListQuery = useGetUserListQuery(undefined, {});
    const [updateAuctionMutation, updateAuctionMutationData] =
        useUpdateAuctionMutation();

    const isLoading =
        auctionByUrlQuery.isLoading ||
        getUserListQuery.isLoading ||
        updateAuctionMutationData.isLoading;

    const isDisabled = auctionByUrlQuery.data?.isCompleted;

    useEffect(() => {
        if (auctionByUrlQuery.data) {
            setValue(
                "isApprovalRequired",
                auctionByUrlQuery.data.isApprovalRequired
            );
            setValue(
                "approvedBidders",
                auctionByUrlQuery.data.approvedAuctionBidderUserIds
            );
        }
    }, [auctionByUrlQuery.data, setValue]);

    const resetFormErrors = () =>
        setError("isApprovalRequired", { message: "" });

    const onSubmit = async () => {
        try {
            resetFormErrors();

            if (!auctionByUrlQuery?.data?.auctionId) {
                throw new Error("Auction not found");
            }

            const patch = createPatch(
                {
                    isApprovalRequired:
                        auctionByUrlQuery?.data?.isApprovalRequired,
                    approvedAuctionBidderUserIds:
                        auctionByUrlQuery?.data?.approvedAuctionBidderUserIds
                },
                {
                    isApprovalRequired: isApprovalRequiredFormValue,
                    approvedAuctionBidderUserIds: approvedBiddersFormValue
                }
            );

            if (patch.length > 0) {
                await updateAuctionMutation({
                    auctionId: auctionByUrlQuery?.data?.auctionId || "",
                    patch
                }).unwrap();
            }

            // Reloads auction details for this tab
            auctionByUrlQuery.refetch();
            // Reload auction details for the "Auction Details" tab, so saving there will not overwrite changes made here
            dispatch(AuctionStore.actionCreators.loadAuctionDetail(auctionUrl));

            enqueueSnackbar("Approved bidders successfully saved", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                preventDuplicate: false,
                variant: "success"
            });
        } catch (e) {
            console.error(e);

            enqueueSnackbar("Approved bidders failed to save", {
                anchorOrigin: { horizontal: "left", vertical: "top" },
                preventDuplicate: false,
                variant: "error"
            });

            if (e instanceof z.ZodError) {
                return setError("isApprovalRequired", {
                    message: e.errors[0].message
                });
            }
            if (e instanceof Error) {
                return setError("isApprovalRequired", {
                    message: (e as Error).message
                });
            }
            return setError("isApprovalRequired", {
                message: "Unknown error occurred while saving"
            });
        }
    };

    const formatFullName = (bidder: IUserInfo) =>
        `${bidder.firstName} ${bidder.lastName}`;

    const filterOptions = (
        options: IUserInfo[],
        { inputValue }: { inputValue: string }
    ) =>
        matchSorter(options, inputValue, {
            keys: ["firstName", "lastName", "email"]
        });

    const potentialBidders = getUserListQuery?.data?.bidders || [];
    return (
        <>
            <div className={classes.formSection} />
            <div className={classes.formSection}>
                <FormControlLabel
                    label="Approval Mode"
                    control={
                        <Controller
                            name="isApprovalRequired"
                            control={control}
                            render={({ field }) => (
                                <Switch
                                    {...field}
                                    disabled={isDisabled || auctionLive}
                                    checked={field.value}
                                    color="primary"
                                />
                            )}
                        />
                    }
                />
                <FormHelperText>
                    If approval mode is enabled, this auction will require
                    bidders to be approved.
                </FormHelperText>
                {isApprovalRequiredFormValue && (
                    <Autocomplete
                        multiple
                        options={potentialBidders}
                        className={classes.allowedBidderSelect}
                        disableCloseOnSelect
                        disabled={isDisabled}
                        autoComplete={false}
                        value={potentialBidders.filter((b) =>
                            approvedBiddersFormValue.includes(b.id)
                        )}
                        onChange={(_, newValue) => {
                            setValue(
                                "approvedBidders",
                                newValue.map((b) => b.id)
                            );
                        }}
                        getOptionLabel={(bidder) => formatFullName(bidder)}
                        renderOption={(bidder, { selected }) => (
                            <MenuItem
                                key={bidder.id}
                                value={bidder.id}
                                className={classes.allowedBidderMenuItem}
                            >
                                <Checkbox checked={selected} color="primary" />
                                <ListItemText
                                    primary={formatFullName(bidder)}
                                    secondary={bidder.email}
                                />
                            </MenuItem>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label="Approved Bidders"
                                placeholder="Search by name or email"
                            />
                        )}
                        filterOptions={filterOptions}
                    />
                )}

                <LoaderButton
                    className={classes.saveButton}
                    disabled={isDisabled}
                    loading={isLoading}
                    size="large"
                    variant="contained"
                    color="primary"
                    onClick={onSubmit}
                >
                    <Save />
                    &nbsp;Save
                </LoaderButton>
            </div>
        </>
    );
}

const reduxState = (state: IAppState) =>
    Object.assign({}, state.auctionStoreState);
const reduxDispatch = (dispatch: IAppDispatch) =>
    bindActionCreators(
        Object.assign({}, AuctionStore.actionCreators),
        dispatch
    );

export default withStyles(styles)(
    connect(reduxState, reduxDispatch)(ApprovedAuctionBiddersTab)
);
